import React, { ChangeEvent, ReactNode, useContext, useState } from 'react';
import { Layout } from '../Layout';
import { IntlShape, useIntl } from 'react-intl';
import { SelectItem } from 'primereact/selectitem';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../Auth/components/AuthProvider';
import { ToastContext, ToastContextType } from '../../util/providers/ToastProvider';
import { Loading } from '../system/Loading';
import { Link, NavigateFunction, useNavigate } from 'react-router';
import { SearchServiceApi } from '../../api/import/apis/SearchServiceApi';
import { ImportServiceApi } from '../../api/import/apis/ImportServiceApi';
import { SearchResultDTO, WorkDTO } from 'api/import/models';
import { ProgressSpinner } from 'primereact/progressspinner';

const importTypesSelect: SelectItem[] = [
  {label: 'DOI', value: 'DOI'},
  {label: 'ISBN', value: 'ISBN'},
];

export type ImportType = 'DOI' | 'ISBN'

export const ImportPage = (): ReactNode => {
  const toast: ToastContextType = useContext(ToastContext);
  const {actions} = useContext(AuthContext);
  const intl: IntlShape = useIntl();
  const [results, setResults] = useState<SearchResultDTO[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const navigate: NavigateFunction = useNavigate();
  const importSearchApi: SearchServiceApi = new SearchServiceApi().withMiddleware(getAuthMiddleware(actions));
  const importApi: ImportServiceApi = new ImportServiceApi().withMiddleware(getAuthMiddleware(actions));
  const [importType, setImportType] = useState<ImportType>('DOI');
  const [importId, setImportId] = useState<string>('');

  const formatIssuedDate = (value: string): string => {
    if(value.length === 4) {
      return value;
    }

    if(value.length === 8) {
      const year = value.substring(0,4);
      const month = value.substring(4,6);
      const day = value.substring(6,8);

      if(month !== '00' && day !== '00') {
        return `${day}.${month}.${year}`
      } else {
        return year;
      }
    }
    return value;
  }

  const search = (event: React.SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setLoadingSearch(true);

    if(importType === 'DOI') {
      importSearchApi.getWorksByDoi({ doi: importId })
      .then((result: SearchResultDTO[]) => {
        if (result.length < 1) {
          toast.notify({
            summary: intl.formatMessage({id: 'work.import.alert.noResults'}),
            severity: 'warn',
            sticky: false,
            life: 3000,
          });
        }
        setResults(result);
      })
      .catch((): void => {
        toast.notify({
          severity: 'error',
          summary: intl.formatMessage({id: 'work.import.error.title'}),
          detail: intl.formatMessage({id: 'work.import.error.message'}),
        });
      })
      .finally((): void => setLoadingSearch(false));
    } else if(importType === 'ISBN') {
      importSearchApi.getWorksByIsbn({ isbn: importId })
      .then((result: SearchResultDTO[]) => {
        if(result.length < 1) {
          toast.notify({
            summary: intl.formatMessage({id: 'work.import.alert.noResults'}),
            severity: 'warn',
            sticky: false,
            life: 3000
          });
        }
        setResults(result);
      })
      .catch((): void => {
        toast.notify({
          severity: 'error',
          summary: intl.formatMessage({id: 'work.import.error.title'}),
          detail: intl.formatMessage({id: 'work.import.error.message'})
        });
      })
      .finally((): void => setLoadingSearch(false));
    }
  };

  const importWork = (workDTO: WorkDTO): void => {
    setLoadingImport(true);

    if(importType === 'DOI') {
      importApi.startCrossrefJob({ workDTO: workDTO })
      .then((result: WorkDTO) => {
        navigate('/work/add', { state: {import: result} })
      })
      .catch((): void => {
        toast.notify({
          severity: 'error',
          summary: intl.formatMessage({id: 'work.import.error.title'}),
          detail: intl.formatMessage({id: 'work.import.error.message'})
        });
      })
      .finally((): void => setLoadingImport(false));
    } else if(importType === 'ISBN') {
      importApi.startGVIJob({ workDTO: workDTO })
      .then((result: WorkDTO) => {
        navigate('/work/add', { state: {import: result}})
      })
      .catch((): void => {
        toast.notify({
          severity: 'error',
          summary: intl.formatMessage({id: 'work.import.error.title'}),
          detail: intl.formatMessage({id: 'work.import.error.message'})
        });
      })
      .finally((): void => setLoadingImport(false));
    }
  }

  return (
    <Layout
      title={intl.formatMessage({id: 'import.title'})}
      mainTitle={intl.formatMessage({id: 'work.import.headline'})}
    >
      <div className={'flex flex-column px-3 gap-4'}>
        <div>
          <Button
            disabled={loadingSearch}
            label={intl.formatMessage({id: 'button.back'})}
            icon={'pi pi-arrow-left'}
            onClick={(): void => void navigate('/work/add')}
            className='bg-white text-primary'
          />
        </div>
        <form
          onSubmit={search}
          className={'flex justify-content-center flex-column md:flex-row gap-4 w-12'}
        >
          <Dropdown
            id={'import_type'}
            autoFocus={true}
            value={importType}
            required={true}
            onChange={(event: DropdownChangeEvent): void => setImportType(event.value)}
            options={importTypesSelect}
          />
          <div className={'md:w-6 p-fluid'}>
            <InputText
              id={'import_id'}
              value={importId}
              required={true}
              invalid={!importId || importId.trim().length < 1}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => setImportId(event.target.value)}
              placeholder={intl.formatMessage({id: 'work.import.input.placeholder'})}
            />
          </div>
          <Button disabled={loadingSearch || loadingImport} label={intl.formatMessage({id: 'button.search'})}/>
        </form>
        {loadingImport && (
          <div
            className="absolute top-0 left-0 w-full h-full flex align-items-center justify-content-center"
            style={{
              background: 'rgba(255, 255, 255, 0.6)',
              zIndex: 10
            }}
          >
            <div style={{ display: 'inline-grid', gap: '1em' }}>
              <ProgressSpinner
                style={{ width: '50px', height: '50px' }}
              />
              <div className='text-xl text-primary font-bold' style={{ justifyItems: 'center' }}>
                <div>{intl.formatMessage({id: 'work.import.loading.firstLine'})}</div>
                <div>{intl.formatMessage({id: 'work.import.loading.secondLine'})}</div>
              </div>
            </div>
          </div>
        )}
        {loadingSearch && <Loading/>}
        {!loadingSearch && results.length > 0 && (
          <div className={'flex flex-column gap-4'}>
            <h3>{intl.formatMessage({id: 'work.import.results'})}</h3>
            {results.map((result: SearchResultDTO, index: number): ReactNode => (
              <div className="p-inputgroup flex gap-4 align-items-center" key={index}>
                <Button
                  className={`w-2rem h-2rem flex-grow-0 flex-shrink-0`}
                  title={intl.formatMessage({id: 'button.import'})}
                  icon={'pi pi-file-import'}
                  onClick={() => importWork(result.workDTO)}
                  disabled={result.mmsId !== undefined}
                />
                <div>
                  <div>{result.workDTO.title || intl.formatMessage({id: 'work.import.results.missingTitle'})}</div>
                  <div className='flex' style={{ fontStyle: "italic" }}>
                    {[
                      result.workDTO?.workPersons
                        ?.find(person => person.sequence === 1)
                        ? `${result.workDTO.workPersons.find(person => person.sequence === 1)?.personName.givenname} ${result.workDTO.workPersons.find(person => person.sequence === 1)?.personName.surname}`
                        : null,

                      result.workDTO?.workPersonOthers
                        ?.find(person => person.sequence === 1)
                        ? `${result.workDTO.workPersonOthers.find(person => person.sequence === 1)?.givenname} ${result.workDTO.workPersonOthers.find(person => person.sequence === 1)?.surname}`
                        : null,

                      result.workDTO?.issued
                        ? formatIssuedDate(result.workDTO.issued.toString())
                        : null,

                      result.workDTO?.edition
                        ? result.workDTO.edition
                        : null
                      ].filter(Boolean).join(', ')
                    }
                  </div>
                  {result.mmsId &&
                    <div className='text-red-500 flex gap-2'>
                      <div>{intl.formatMessage({id: 'work.import.results.exist'})}</div>
                      <div>
                        <Link target="_blank" to={`/work/${result.mmsId}`} className='text-primary-800 mr-1'>
                          {result.mmsId}
                        </Link>
                        <i className="pi pi-external-link text-primary-800" style={{ fontSize: '13px' }}></i>
                      </div>
                    </div>
                  }
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ImportPage;
